import { useState, useRef } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import './SignInPopup.css';
import {
    requestOTP,
    verifyOTP,
    createUserGoogle,
    setToken as setCoreToken
} from '@genfun/core/api/auth';
import { RedButton, RedInput, GoogleButton } from '../components/common';

function CloseIcon() {
    return (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M1 1L11 11M1 11L11 1" stroke="#FFB2C6" strokeWidth="2" strokeLinecap="round"/>
        </svg>
    );
}

// Shared function for requesting OTP code
const handleResendCode = async (email, setError, setResending = null, setAwaitingCode = null, ref = null) => {
    if (setResending) setResending(true);
    try {
        const [hasError, status] = await requestOTP(email, ref);
        if (hasError) {
            if (status === 404) {
                setError("User not found");
            } else if (status === 400) {
                setError("User is registered through Google");
            } else {
                setError("Failed to send OTP. Please try again.");
            }
            return false;
        } else {
            if (setAwaitingCode) {
                setError(null);
                setAwaitingCode(true);
            } else {
                setError("Code resent successfully!");
                setTimeout(() => setError(null), 3000);
            }
            return true;
        }
    } catch (err) {
        setError("An error occurred. Please try again.");
        console.error(err);
        return false;
    } finally {
        if (setResending) setResending(false);
    }
};

async function googleAction(creds, setError, setToken, onSuccess, ref = null) {
    const resp = await createUserGoogle(creds, ref);
    setCoreToken(resp.access_token);
    if (resp.access_token === undefined && resp.detail !== undefined) {
        setError(resp.detail);
    } else {
        if (setToken) setToken(resp.access_token);
        if (onSuccess) onSuccess();
    }
}

function EmailStage({ emailRef, setAwaitingCode, setError, error, loading, setLoading, setToken, onSuccess }) {
    const ref = localStorage.getItem('referral_code');

    const login = useGoogleLogin({
        onSuccess: tokenResponse => googleAction(tokenResponse, setError, setToken, onSuccess, ref),
        onError: () => setError("Google login failed. Please try again.")
    });

    const handleEmailSubmit = async () => {
        const email = emailRef.current?.value;
        if (!email || email.trim() === "") {
            setError("Please enter your email");
            return;
        }

        setLoading(true);
        await handleResendCode(email, setError, null, setAwaitingCode, ref);
        setLoading(false);
    };

    return (
        <div className="signInContent">
            <div className="signInCard">
                <RedInput
                    inputRef={emailRef}
                    label="Email"
                    placeholder="user@example.com"
                    type="email"
                />
                <RedButton onClick={handleEmailSubmit} disabled={loading}>
                    {loading ? "Sending..." : "Continue with email"}
                </RedButton>
            </div>
            <div className="signInCard">
                <GoogleButton onClick={() => login()} />
            </div>
            {error && <div className="signInError">{error}</div>}
        </div>
    );
}

function OTPStage({ email, setAwaitingCode, setError, error, setToken, onSuccess }) {
    const otpRef = useRef(null);
    const [resending, setResending] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleVerify = async () => {
        const code = otpRef.current?.value;
        if (!code || code.trim() === "") {
            setError("Please enter the verification code");
            return;
        }

        setLoading(true);
        try {
            const [response, status] = await verifyOTP(email, code);

            if (status === 200) {
                if (response && response.access_token) {
                    setCoreToken(response.access_token);
                    if (setToken) setToken(response.access_token);
                }
                if (onSuccess) onSuccess();
            } else {
                if (response && response.detail) {
                    setError(response.detail);
                } else {
                    setError("Invalid verification code. Please try again.");
                }
            }
        } catch (err) {
            setError("An error occurred. Please try again.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="signInContent">
            <div className="signInCard">
                <div className="signInOtpHeader">
                    <div
                        className="signInBackArrow"
                        onClick={() => {
                            setError(null);
                            setAwaitingCode(false);
                        }}
                    >
                        <img src={`${process.env.PUBLIC_URL}/arrow-left.svg`} alt="Back" />
                    </div>
                    <div className="signInOtpInfo">
                        We've sent you a code to: <span className="signInOtpEmail">{email}</span>
                    </div>
                    <div className="signInOtpInfo">Please check your inbox and spam folder</div>
                </div>
                <RedInput
                    inputRef={otpRef}
                    label="Code from email"
                    placeholder="Enter code"
                    type="text"
                />
                <span
                    className="signInResendLink"
                    onClick={() => handleResendCode(email, setError, setResending)}
                >
                    {resending ? "Sending..." : "Resend code"}
                </span>
                {error && <div className="signInError">{error}</div>}
                <RedButton onClick={handleVerify} disabled={loading}>
                    {loading ? "Verifying..." : "Log in"}
                </RedButton>
            </div>
        </div>
    );
}

export default function SignInPopup({ isOpen, setIsOpen, setToken }) {
    const emailRef = useRef(null);
    const [awaitingCode, setAwaitingCode] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const handleClose = () => {
        setIsOpen(false);
        // Reset state when closing
        setAwaitingCode(false);
        setError(null);
    };

    const handleSuccess = () => {
        handleClose();
    };

    return (
        <div className="signInPopupOverlay" onClick={handleClose}>
            <div className="signInPopupPanel" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="signInHeader">
                    <h2 className="signInTitle">Sign in to continue</h2>
                    <button className="signInCloseBtn" onClick={handleClose}>
                        <CloseIcon />
                    </button>
                </div>

                {/* Content */}
                {!awaitingCode ? (
                    <EmailStage
                        emailRef={emailRef}
                        setAwaitingCode={setAwaitingCode}
                        setError={setError}
                        error={error}
                        loading={loading}
                        setLoading={setLoading}
                        setToken={setToken}
                        onSuccess={handleSuccess}
                    />
                ) : (
                    <OTPStage
                        email={emailRef.current?.value}
                        setAwaitingCode={setAwaitingCode}
                        setError={setError}
                        error={error}
                        setToken={setToken}
                        onSuccess={handleSuccess}
                    />
                )}
            </div>
        </div>
    );
}
